import clsx from 'clsx'
import { GetStaticProps, GetStaticPaths } from 'next'

/* blocks */
import Layout from '@/templates/Layout/CMLLayout'
import InfluencerDetailsHero from '@/templates/Influencer/InfluencerDetailsHero'
import InfluencerDetailsAsideContent from '@/templates/Influencer/InfluencerDetailsAsideContent'
import InfluencerGetStarted from '@/templates/Influencer/InfluencerGetStarted'

/* utils */
import {
  getStrapiGraphqlData,
  getWordpressGraphqlData
} from '@/utils/api/wrapper'
import { influencerDetailsPageQuery } from '@/utils/queries/influencer'
import blogListQuery from '@/utils/queries/blog_list'
import { INSTANT_REVALIDATION } from '@/utils/constants/revalidation'
import { capture404PageError } from '@/utils/functions/elk-server'

/* styling */
import styles from './_.module.css'

interface Props {
  currentInfluencer: Influencer
  posts: BlogPostList[]
  getStarted: StrapiBlockComponent
}

function InfluencerDetailsPage({
  currentInfluencer,
  posts,
  getStarted
}: Props): JSX.Element {
  const metaData = [
    {
      property: 'og:title',
      content: currentInfluencer.Seo.title
    },
    {
      property: 'description',
      content: currentInfluencer.Seo.description ?? ''
    },
    {
      property: 'og:description',
      content: currentInfluencer.Seo.description ?? ''
    }
  ]

  return (
    <Layout
      title={currentInfluencer.Seo.title}
      canonical={`/global-marketing-leaders/${currentInfluencer.slug}/`}
      meta={metaData}
    >
      <InfluencerDetailsHero
        image={currentInfluencer.image.data?.attributes}
        name={currentInfluencer.name}
        designation={currentInfluencer.designation}
        themeColor={currentInfluencer.themeColor}
        links={{
          instagram: currentInfluencer.instagramLink,
          twitter: currentInfluencer.twitterLink,
          linkedin: currentInfluencer.linkedinLink
        }}
        slug={`/global-marketing-leaders/${currentInfluencer.slug}/`}
      />

      <section className="max-width-container | grid grid-cols-1 lg:grid-cols-12 gap-8 | mb-10">
        <div
          className={clsx(
            styles.bio_section,
            'prose lg:prose-lg prose-strong:font-medium prose-headings:font-medium  | col-span-full lg:col-span-8 | max-w-2xl'
          )}
          dangerouslySetInnerHTML={{ __html: currentInfluencer.bio }}
        />

        <aside className="col-span-full lg:col-span-4 | lg:ml-auto">
          <InfluencerDetailsAsideContent posts={posts} />
        </aside>
      </section>

      <InfluencerGetStarted
        primaryHeadline={getStarted.title ?? ''}
        subText={getStarted.description ?? ''}
        image={getStarted.multimedia?.media?.data?.attributes}
        primaryCta={getStarted.link}
        mode="full_width"
        variant="purple"
      />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug

  const currentInfluenceResult =
    await getStrapiGraphqlData<IndividualInfluencerPage>(
      influencerDetailsPageQuery.loc?.source.body,
      { slug }
    )

  if (!currentInfluenceResult.influencers.data[0]) {
    capture404PageError(`/global-marketing-leaders/$${slug}/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  const currentInfluencer =
    currentInfluenceResult.influencers.data[0]?.attributes

  const getStarted =
    currentInfluenceResult.influencerPage.data.attributes.getStarted

  const wpResult = await getWordpressGraphqlData<{
    posts: { nodes: BlogPostList[] }
  }>(blogListQuery({ limit: 4, fetchFeaturedPost: false }).loc?.source.body)
  const posts = wpResult.posts.nodes

  return {
    props: {
      currentInfluencer,
      posts,
      getStarted
    },
    revalidate: INSTANT_REVALIDATION
  }
}
export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}

export default InfluencerDetailsPage
